import { CircularProgress } from '@material-ui/core';
import _ from 'lodash';
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ItemContent from '../../../../components/ItemContent/ItemContent';
import CustomLabel from '../../../../components/LabelWithPars/components/CustomLabel';
import { EPaths } from '../../../../types/enums/EPaths';
import { EReferralCodesRewardReceivers } from '../../../../types/enums/EReferralCodesRewardReceivers';
import { IEditForm } from '../../../../types/interfaces/IEditForm';
import { NSConfigs } from '../../../../types/interfaces/NSConfigs';
import { endpoints } from '../../../../utils/endpoints';
import StringsFormater from '../../../../utils/stringsFromater';
import tPaths from '../../../../utils/tPaths';
import useDescLangs from '../../../../utils/useDescLangs';
import { EReward } from '../../../../types/enums/EReward';
import useApi from '../../../../utils/useApi';

function fieldsOfFirstLanguage(fields: any) {
  const lang = Object.keys(fields)[0];
  return fields[lang];
}
const ReferralCodes = ({ editable }: { editable: boolean }) => {
  const { languagesList, reduceLangsList } = useDescLangs();
  const { t } = useTranslation();
  const { data: freePlans, isFetching } = useApi<NSConfigs.IFreePlanDetails[]>(endpoints.freePlans);

  const [rewardType, setRewardType] = useState<EReward | null>(null);

  const descriptionMapper = useCallback(
    (data: NSConfigs.IData): IEditForm[][] => {
      const type = rewardType || data.referralCodes.currentRewardType;
      return [
        [
          {
            type: 'input',
            fieldName: 'timesToApplyACode',
            label: <CustomLabel label={t(tPaths.configs.timesToApplyACode)} />,
            initialValue: data.referralCodes.timesToApplyACode,
          },
          {
            type: 'input',
            fieldName: 'rewardReceivers',
            label: <CustomLabel label={t(tPaths.configs.rewardReceivers)} />,
            initialValue: EReferralCodesRewardReceivers.Inviter,
          },
          {
            type: 'input',
            fieldName: 'termsOfUse',
            multiline: true,
            label: <CustomLabel label={t(tPaths.global.termsOfUse)} />,
            initialValue: new StringsFormater(data.referralCodes.termsOfUse).merge(),
          },
          {
            type: 'input',
            fieldName: 'inviteFriendMessage',
            multiline: true,
            label: <CustomLabel label={t(tPaths.global.inviteFriendMessage)} />,
            initialValue: data.referralCodes.inviteFriendMessage,
          },
          {
            type: 'input',
            fieldName: 'rewardingThreshold',
            label: <CustomLabel label={t(tPaths.configs.rewardThreshold)} />,
            initialValue: data.referralCodes.rewardingThreshold.amount.toString(),
          },
          {
            type: 'select',
            fieldName: 'rewardType',
            label: <CustomLabel label={t(tPaths.configs.rewardType)} />,
            variants: data.referralCodes.rewardTypes.map((v) => ({ value: v, label: v })),
            initialValue: data.referralCodes.currentRewardType,
            onChange: (_form, value) => setRewardType(value),
          },
          {
            type: 'select',
            fieldName: 'freePlanCarrierIdentifier',
            label: <CustomLabel label={t(tPaths.configs.freePlan)} />,
            variants: (freePlans || []).map((v) => ({
              value: v.carrierIdentifier,
              label: v.title,
            })),
            initialValue: data.referralCodes.rewards[EReward.FreePlan].carrierIdentifier,
            hidden: type !== EReward.FreePlan,
          },
          // {
          //   type: 'input',
          //   fieldName: 'balance',
          //   label: <CustomLabel label={t(tPaths.configs.balance)} />,
          //   value: data.referralCodes.rewards[EReward.BalanceTopUp].balance.amount.toString(),
          //   initialValue: data.referralCodes.rewards[EReward.BalanceTopUp].balance.amount.toString(),
          //   hidden: type !== EReward.BalanceTopUp,
          // },
        ],
      ];
    },
    [t, rewardType, freePlans],
  );

  const tabs = useMemo(
    () => [...languagesList.map((lang) => ({ key: lang.key, label: lang.display }))],
    [languagesList],
  );

  const tabsContentMapper = useCallback(
    (data: NSConfigs.IData) => ({
      ...languagesList.reduce(
        (acc, cv) => ({
          ...acc,
          [cv.key]: descriptionMapper(data),
        }),
        {},
      ),
    }),
    [descriptionMapper, languagesList],
  );

  const updateDataFormatter = useCallback(
    (fieldsWithLanguages: any, data: NSConfigs.IData) => {
      const fields = fieldsOfFirstLanguage(fieldsWithLanguages);

      const type = fields['rewardType'].value as EReward;

      const getReward = () => {
        const rewardFor: Record<EReward, () => NSConfigs.TReward> = {
          [EReward.FreePlan]: () => ({
            carrierIdentifier: fields['freePlanCarrierIdentifier'].value,
          }),
          // [EReward.BalanceTopUp]: () => ({
          //   balance: {
          //     ...data.referralCodes.rewards[EReward.BalanceTopUp].balance,
          //     amount: fields['balance'].value,
          //   },
          // }),
        };

        const result = rewardFor[type];

        if (!result) throw new Error('Could not save. Unknown reward type: ' + type);

        return result();
      };

      return {
        configs: {
          ...data,
          referralCodes: {
            ...data.referralCodes,
            rewardingThreshold: {
              ...data.referralCodes.rewardingThreshold,
              amount: fields['rewardingThreshold'].value,
            },
            rewardReceivers: fields['rewardReceivers'].value,
            timesToApplyACode: fields['timesToApplyACode'].value,
            termsOfUse: new StringsFormater([fields['termsOfUse'].value]).split(),
            inviteFriendMessage: fields['inviteFriendMessage'].value,
            currentRewardType: type,
            rewards: {
              ...data.referralCodes.rewards,
              [type]: getReward(),
            },
          },
        },
      };
    },
    [reduceLangsList],
  );

  const onChangeFormatter = useCallback(({ fieldName, value }) => {
    return _.includes(['rewardingThreshold', 'balance'], fieldName) ? parseInt(value) : value;
  }, []);

  return (
    <>
      {languagesList.length && !isFetching ? (
        <ItemContent
          path={EPaths.LegalLinks}
          title={t(tPaths.configs.title)}
          tabs={tabs}
          tabsContentMapper={tabsContentMapper}
          updateDataFormatter={updateDataFormatter}
          onChangeFormatter={onChangeFormatter}
          apiUrl={endpoints.configs()}
          backButton={false}
          editable={editable}
        />
      ) : (
          <CircularProgress />
        )}
    </>
  );
};

export default ReferralCodes;
